import { Box, Paragraph, Table, TextButton } from '@zillow/constellation-10';
import { MissingAttributesModal } from './MissingAttributesModal';
import React, { useState } from 'react';
import { SalesProCard } from '../../../SharedComponents/SalesProCard';
import { useMainLayout } from '../../../layouts/MainPageLayout/MainLayoutContext';
import type { ListingQualityIssuesDto } from '@zg-rentals/ts-customer-analytics-client';
import listingQualitiesIcon from '../../../../img/listingQualities.svg';

import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitOverviewListingQualityFieldEvent } from '../../../../analytics/events';
import { isClaimedProperty } from '../../../../utils/isClaimedProperty';

export interface MissingItem {
  name: string;
  modalHeader: string;
  properties: Array<ListingQualityIssuesDto>;
  key: keyof ListingQualityIssuesDto;
}

export const OverviewReportSidebar = () => {
  const { listingQualityScores, filteredProperties, selectedFilters } = useMainLayout();
  const { user } = useRentalManagerUser();

  const qualityGroups: Array<MissingItem> = React.useMemo(() => {
    const promotedListingAliases = new Set(
      filteredProperties
        .filter((property) => !isClaimedProperty(property) && property.currentPublishedStatus === 'Published')
        .map((property) => property.aggregateStats.currentListingAlias),
    );

    const missingItemConfigs: Array<MissingItem> = [
      {
        key: 'hasAdministrativeFee',
        name: 'Administrative fee details',
        modalHeader: 'Properties missing administrative fee details',
        properties: [],
      },
      {
        key: 'hasAmenities',
        name: 'Amenity details',
        modalHeader: 'Properties missing amenity details',
        properties: [],
      },
      {
        key: 'hasApplicationFee',
        name: 'Application fee info',
        modalHeader: 'Properties missing application fee info',
        properties: [],
      },
      {
        key: 'hasApplications',
        name: 'Zillow applications',
        modalHeader: 'Properties missing Zillow applications',
        properties: [],
      },
      {
        key: 'hasBusinessHours',
        name: 'Business hours',
        modalHeader: 'Properties missing business hours',
        properties: [],
      },
      {
        key: 'hasContact',
        name: 'Contact information',
        modalHeader: 'Properties missing contact information',
        properties: [],
      },
      {
        key: 'hasDateAvailable',
        name: 'Date available',
        modalHeader: 'Properties missing date available',
        properties: [],
      },
      {
        key: 'hasDescription',
        name: 'Description',
        modalHeader: 'Properties missing description',
        properties: [],
      },
      {
        key: 'hasFloorPlanLayout',
        name: 'Floor plan layout',
        modalHeader: 'Properties missing floor plan layout',
        properties: [],
      },
      {
        key: 'hasFloorPlanPhotos',
        name: 'Floor plan photos',
        modalHeader: 'Properties missing floor plan photos',
        properties: [],
      },
      {
        key: 'hasHidePhone',
        name: 'Phone number hidden',
        modalHeader: 'Properties with phone number hidden',
        properties: [],
      },
      {
        key: 'hasHideStreetAddress',
        name: 'Street address hidden',
        modalHeader: 'Properties with street address hidden',
        properties: [],
      },
      {
        key: 'hasLaundry',
        name: 'Laundry information',
        modalHeader: 'Properties missing laundry information',
        properties: [],
      },
      {
        key: 'hasLeaseLength',
        name: 'Lease length',
        modalHeader: 'Properties missing lease length',
        properties: [],
      },
      {
        key: 'hasLeaseTerms',
        name: 'Lease terms',
        modalHeader: 'Properties missing lease terms',
        properties: [],
      },
      {
        key: 'hasMonthlyFee',
        name: 'Monthly fees',
        modalHeader: 'Properties missing monthly fees',
        properties: [],
      },
      {
        key: 'hasOneTimeFee',
        name: 'One-time fees',
        modalHeader: 'Properties missing one-time fees',
        properties: [],
      },
      {
        key: 'hasParking',
        name: 'Parking information',
        modalHeader: 'Properties missing parking information',
        properties: [],
      },
      {
        key: 'hasPetsNotAllowed',
        name: 'Pet policy details',
        modalHeader: 'Properties missing pet policy details',
        properties: [],
      },
      {
        key: 'hasPhotos',
        name: '10+ photos',
        modalHeader: 'Properties missing 10+ photos',
        properties: [],
      },
      {
        key: 'hasPrice',
        name: 'Price details',
        modalHeader: 'Properties missing price details',
        properties: [],
      },
      {
        key: 'hasSchedule',
        name: 'Tour scheduling',
        modalHeader: 'Properties missing tour scheduling',
        properties: [],
      },
      {
        key: 'hasSecurityDeposit',
        name: 'Security deposit details',
        modalHeader: 'Properties missing security deposit details',
        properties: [],
      },
      {
        key: 'hasSpecialOffer',
        name: 'Special offer',
        modalHeader: 'Properties missing special offer',
        properties: [],
      },
      {
        key: 'hasSquareFootage',
        name: 'Square footage',
        modalHeader: 'Properties missing square footage',
        properties: [],
      },
      {
        key: 'hasUnitAvailability',
        name: 'Unit availability',
        modalHeader: 'Properties missing unit availability',
        properties: [],
      },
      {
        key: 'hasVideo',
        name: 'Video',
        modalHeader: 'Properties missing video',
        properties: [],
      },
      {
        key: 'hasVirtualTour',
        name: '3D tour',
        modalHeader: 'Properties missing 3D tour',
        properties: [],
      },
      {
        key: 'hasWebsite',
        name: 'Website information',
        modalHeader: 'Properties missing website information',
        properties: [],
      },
    ];

    for (const listingQuality of Object.values(listingQualityScores)) {
      if (!promotedListingAliases.has(listingQuality.listingAlias)) {
        continue;
      }
      missingItemConfigs.forEach((itemConfig) => {
        if (listingQuality[itemConfig.key] === false) {
          itemConfig.properties.push(listingQuality);
        }
      });
    }
    return missingItemConfigs;
  }, [filteredProperties, listingQualityScores]);

  const [isMissingAttributesModalOpen, setIsMissingAttributesModalOpen] = useState(false);
  const [missingItem, setMissingItem] = useState<MissingItem | null>(null);

  const hasRecommendations = qualityGroups.some((item) => item.properties.length > 0);

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      }}
    >
      <Box css={{ paddingInline: '400' }}>
        {hasRecommendations ? (
          <>
            <Paragraph>Get more out of your promoted listings by adding the details renters prioritize.</Paragraph>
            <Box css={{ marginInline: '-400' }}>
              <Table.Root aria-label="Listing details table" appearance="horizontal">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell css={{ paddingLeft: '24px' }}>What&apos;s missing</Table.HeaderCell>
                    <Table.HeaderCell css={{ textAlign: 'right', paddingRight: '24px' }}>
                      # of listings
                    </Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {qualityGroups
                    .filter((item) => item.properties.length > 0)
                    .map((item) => (
                      <Table.Row key={item.name}>
                        <Table.Cell css={{ paddingLeft: '24px' }}>
                          <TextButton textStyle="body" asChild>
                            <a
                              onClick={() => {
                                if (user) emitOverviewListingQualityFieldEvent({ user });
                                setIsMissingAttributesModalOpen(true);
                                setMissingItem(item);
                              }}
                            >
                              {item.name}
                            </a>
                          </TextButton>
                        </Table.Cell>
                        <Table.Cell css={{ textAlign: 'right', paddingRight: '24px' }}>
                          {item.properties.length}
                        </Table.Cell>
                      </Table.Row>
                    ))}
                </Table.Body>
              </Table.Root>
            </Box>
            <MissingAttributesModal
              isOpen={isMissingAttributesModalOpen}
              onClose={() => setIsMissingAttributesModalOpen(false)}
              missingItem={missingItem}
            />
          </>
        ) : (
          <Box
            css={{
              marginTop: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '300',
            }}
          >
            <img src={listingQualitiesIcon.src} alt="Listing qualities" />
            <Paragraph>
              {selectedFilters?.listingAlias === ''
                ? 'Your listings in this area have all the details renters look for when considering a property.'
                : 'This property\u2019s listing has all the details renters look for.'}
            </Paragraph>
          </Box>
        )}
      </Box>
      <SalesProCard
        title="Take your listings to the next level"
        description="Chat with your sales pro about how to tailor your listings for each market."
        buttonText="Connect with [sales pro name]"
      />
    </Box>
  );
};
